import { useState } from "react";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSubmitToSii } from "../hooks/useSubmitToSii";
import type { TaxDocumentListItem } from "../types";
import { DTE_TYPE_LABELS } from "../types";
import type { DteTypeCode } from "../types";

interface SubmitToSiiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: TaxDocumentListItem[];
  onSuccess: () => void;
}

type SubmitState = "confirm" | "submitting" | "success" | "error";

function formatCurrency(value: string | null): string {
  if (!value) return "$0";
  const num = parseFloat(value);
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(num);
}

export function SubmitToSiiDialog({
  open,
  onOpenChange,
  documents,
  onSuccess,
}: SubmitToSiiDialogProps) {
  const [state, setState] = useState<SubmitState>("confirm");
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { submitDocuments, isSubmitting } = useSubmitToSii();

  const totalAmount = documents.reduce(
    (sum, doc) => sum + parseFloat(doc.totalAmount || "0"),
    0
  );

  const handleSubmit = async () => {
    setState("submitting");
    setErrorMessage(null);

    try {
      const result = await submitDocuments(documents.map((d) => d.id));
      setTrackingId(result.result.envelopeTrackingId);
      setState("success");
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Error al enviar documentos"
      );
      setState("error");
    }
  };

  const handleClose = () => {
    if (state === "success") {
      onSuccess();
    }
    onOpenChange(false);
    // Reset state after dialog closes
    setTimeout(() => {
      setState("confirm");
      setTrackingId(null);
      setErrorMessage(null);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {state === "confirm" && "Enviar a SII"}
            {state === "submitting" && "Enviando a SII"}
            {state === "success" && "Envío Completado"}
            {state === "error" && "Error de Envío"}
          </DialogTitle>
          <DialogDescription>
            {state === "confirm" &&
              `Se enviarán ${documents.length} documento${documents.length > 1 ? "s" : ""} al SII.`}
            {state === "submitting" && "Por favor espera mientras se procesan los documentos."}
            {state === "success" && "Los documentos han sido enviados exitosamente."}
            {state === "error" && "Hubo un problema al enviar los documentos."}
          </DialogDescription>
        </DialogHeader>

        {state === "confirm" && (
          <div className="space-y-4">
            <div className="max-h-48 overflow-y-auto space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between text-sm p-2 bg-muted rounded"
                >
                  <span>
                    {DTE_TYPE_LABELS[Number(doc.documentTypeCode) as DteTypeCode]} #{doc.folio}
                  </span>
                  <span className="font-medium">
                    {formatCurrency(doc.totalAmount)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm font-medium pt-2 border-t">
              <span>Total:</span>
              <span>
                {new Intl.NumberFormat("es-CL", {
                  style: "currency",
                  currency: "CLP",
                  minimumFractionDigits: 0,
                }).format(totalAmount)}
              </span>
            </div>
          </div>
        )}

        {state === "submitting" && (
          <div className="flex flex-col items-center py-8 gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-sm text-muted-foreground text-center">
              Autenticando con SII...
              <br />
              Firmando sobre...
              <br />
              Subiendo documentos...
            </div>
          </div>
        )}

        {state === "success" && (
          <div className="flex flex-col items-center py-8 gap-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <div className="text-center">
              <p className="font-medium">
                {documents.length} documento{documents.length > 1 ? "s" : ""} enviado
                {documents.length > 1 ? "s" : ""} exitosamente
              </p>
              {trackingId && (
                <p className="text-sm text-muted-foreground mt-1">
                  Track ID: {trackingId}
                </p>
              )}
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center py-8 gap-4">
            <XCircle className="h-12 w-12 text-red-500" />
            <p className="text-sm text-red-600 text-center">{errorMessage}</p>
          </div>
        )}

        <DialogFooter>
          {state === "confirm" && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                Enviar a SII
              </Button>
            </>
          )}
          {(state === "success" || state === "error") && (
            <Button onClick={handleClose}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
